import React, { useCallback, useState } from 'react'

import { F0Dialog, F0Text } from '@factorialco/f0-react'
import { Input, Select, Stack, Textarea } from '@factorialco/f0-react/dist/experimental'
import { useNavigate, useLocation } from 'react-router-dom'

import { CreateTicketDocument, TicketsConnectionDocument } from 'generated/resources/graphql'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useActivity } from 'modules/core/lib/activities/activity'
import { ActivityComponent } from 'modules/core/lib/activities/types'
import { encodeActivity } from 'modules/core/lib/activities/utils'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { type Team, TeamSelector } from 'modules/ticketing/components/TeamSelector'
import {
  getTicketPriorityLabel,
  getTicketPriorityOptions
} from 'modules/ticketing/lib/ticketPriority'

type Data = {
  title: string
  description: string
  priority: string
  assigneeTeamId: string
  assigneeTeamName: string
  assigneeMemberId: string
  assigneeMemberName: string
  slaHours: string
}

export const CreateTicketActivity: ActivityComponent<Data> = ({
  title: initialTitle,
  description: initialDescription,
  priority: initialPriority,
  assigneeTeamId: initialAssigneeTeamId,
  assigneeTeamName: initialAssigneeTeamName,
  assigneeMemberId: initialAssigneeMemberId,
  slaHours: initialSlaHours
}) => {
  const { open, closeActivity } = useActivity()
  const navigate = useNavigate()
  const { search } = useLocation()
  const { company } = useCurrentSession()

  const [title, setTitle] = useState(initialTitle || '')
  const [description, setDescription] = useState(initialDescription || '')
  const validPriorities = new Set<string>(getTicketPriorityOptions())
  const [priority, setPriority] = useState(
    validPriorities.has(initialPriority) ? initialPriority : 'medium'
  )
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(
    initialAssigneeTeamId
      ? { id: initialAssigneeTeamId, name: initialAssigneeTeamName || '' }
      : null
  )
  const [assigneeMemberId] = useState<string | null>(initialAssigneeMemberId || null)
  const [slaHours] = useState<number | null>(
    initialSlaHours ? Number(initialSlaHours) : null
  )
  const [submitError, setSubmitError] = useState<string | null>(null)

  const [performCreate, { loading: creating }] = useMutation(CreateTicketDocument, {
    extractorFn: (data) => data.ticketingMutations.createTicketingTicket
  })

  const handleSubmit = useCallback(async () => {
    const trimmedTitle = title.trim()
    if (!trimmedTitle) {
      setSubmitError(i18n.t('ticketing.inbox.create.title_required'))
      return
    }
    if (!selectedTeam) {
      setSubmitError(i18n.t('ticketing.inbox.create.team_required'))
      return
    }
    setSubmitError(null)

    const result = await performCreate({
      variables: {
        companyId: company.id,
        title: trimmedTitle,
        description: description.trim() || null,
        priority: priority || null,
        assigneeTeamId: selectedTeam.id,
        assigneeMemberId,
        slaHours
      },
      refetchQueries: [TicketsConnectionDocument]
    })
    const handled = handleResult(result, () => {})
    if (handled.success && handled.data?.ticket != null) {
      const ticketId = handled.data.ticket.id
      // Navigate via hash to the detail activity, which implicitly closes this activity
      navigate({
        search,
        hash: encodeActivity('ticketing.viewTicket', { ticketId: String(ticketId) })
      })
    } else {
      setSubmitError(i18n.t('ticketing.inbox.create.create_error'))
    }
  }, [
    title,
    description,
    priority,
    selectedTeam,
    assigneeMemberId,
    slaHours,
    performCreate,
    company.id,
    navigate,
    search
  ])

  return (
    <F0Dialog
      isOpen={open}
      module={{ id: 'inbox', label: i18n.t('ticketing.inbox.create.new_ticket') }}
      onClose={closeActivity}
      primaryAction={{
        label: i18n.t('ticketing.inbox.create.create_button'),
        onClick: handleSubmit,
        disabled: !title.trim() || !selectedTeam || creating
      }}
    >
      <Stack paddingX="p-4" paddingY="p-4" gap="4" data-testid="ticket-create-form">
        <Input
          label={i18n.t('ticketing.inbox.columns.title')}
          value={title}
          onChange={(value) => setTitle(value)}
          placeholder={i18n.t('ticketing.inbox.create.title_placeholder')}
        />
        <Textarea
          label={i18n.t('ticketing.inbox.columns.description')}
          value={description}
          placeholder={i18n.t('ticketing.inbox.create.description_placeholder')}
          onChange={(value) => setDescription(value)}
          rows={4}
        />
        <Stack className="grid grid-cols-2 gap-4">
          <Select<string>
            label={i18n.t('ticketing.inbox.columns.priority')}
            value={priority}
            onChange={(value: string) => setPriority(value)}
            options={getTicketPriorityOptions().map((p) => ({
              value: p,
              label: getTicketPriorityLabel(p)
            }))}
          />
          <TeamSelector
            value={selectedTeam}
            onSelect={(team) => setSelectedTeam(team)}
            label={i18n.t('ticketing.inbox.columns.assignee_team')}
            mandatory
          />
        </Stack>
        {submitError != null && (
          <Stack>
            <F0Text variant="body" content={submitError} color="critical" />
          </Stack>
        )}
      </Stack>
    </F0Dialog>
  )
}
